"use client";

import Link from "next/link";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { NavGroup } from "./HeaderNavClient";
import { ArrowRight, Beef, ChartNoAxesCombined, FileChartColumn, MapPin, Scale, ShoppingBasket, TrendingUp } from "lucide-react";

interface Props {
  group: NavGroup;
  isActive: (href: string) => boolean;
}

/** Desktop nav grup dropdown'u — hover veya tikla ile acilir, disari tikla/Escape kapatir. */
export function HeaderNavDropdown({ group, isActive }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const groupActive = group.items.some((item) => isActive(item.href));
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [menuTop, setMenuTop] = useState(72);

  const showMenu = () => {
    setMenuTop(triggerRef.current?.getBoundingClientRect().bottom ?? 72);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    const updatePosition = () => setMenuTop(triggerRef.current?.getBoundingClientRect().bottom ?? 72);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={showMenu}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => open ? setOpen(false) : showMenu()}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls="header-prices-menu"
        className={clsx(
          "relative inline-flex items-center gap-1 px-3 py-5 text-[14px] font-medium transition-colors after:absolute after:inset-x-3 after:bottom-3 after:h-0.5 after:origin-center after:bg-(--color-accent) after:transition-transform",
          groupActive
            ? "text-(--color-foreground) after:scale-x-100"
            : "text-(--color-foreground) after:scale-x-0 hover:text-(--color-brand)",
        )}
      >
        {group.label}
        <svg
          width="12"
          height="12"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className={clsx("transition-transform", open && "rotate-180")}
          aria-hidden
        >
          <path d="M5 7.5l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div id="header-prices-menu" role="menu" aria-label="Fiyatlar" className="fixed left-1/2 z-50 w-[min(760px,calc(100vw-2rem))] -translate-x-1/2 overflow-y-auto overscroll-contain rounded-b-[8px] border border-(--color-border-soft) bg-(--color-surface) shadow-(--shadow-card)" style={{ top: menuTop, maxHeight: `calc(100dvh - ${menuTop}px - 8px)` }}>
          <div className="grid grid-cols-2">
            <MenuColumn title="Fiyat türleri" items={group.items.filter((item) => item.section === "discover")} isActive={isActive} onSelect={() => setOpen(false)} />
            <MenuColumn title="Analiz araçları" items={group.items.filter((item) => item.section === "compare")} isActive={isActive} onSelect={() => setOpen(false)} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-(--color-border-soft) bg-(--color-background) px-5 py-4 sm:px-7">
            <div><p className="text-[13px] font-bold">Kurum veya şehir mi arıyorsunuz?</p><p className="mt-0.5 text-[11px] text-(--color-muted)">Hal ve borsa kaynaklarını haritada veya listede bulun.</p></div>
            <Link href="/hal" role="menuitem" onClick={() => setOpen(false)} className="inline-flex min-h-10 items-center gap-2 border border-(--color-border) px-3 text-xs font-bold hover:border-(--color-accent)">Haller ve Borsalar <ArrowRight className="size-3.5" aria-hidden /></Link>
          </div>
        </div>
      )}
    </div>
  );
}

const ICONS = {
  prices: ShoppingBasket,
  borsa: FileChartColumn,
  "canli-hayvan": Beef,
  et: Beef,
  compare: Scale,
  index: TrendingUp,
  map: MapPin,
} as const;

function MenuColumn({ title, items, isActive, onSelect }: {
  title: string;
  items: NavGroup["items"];
  isActive: (href: string) => boolean;
  onSelect: () => void;
}) {
  return (
    <div className="min-w-0 border-r border-(--color-border-soft) p-4 last:border-r-0 sm:p-5">
      <h3 className="mb-2 font-display text-lg font-semibold">{title}</h3>
      <div className="space-y-1">
        {items.map((item) => {
          const Icon = ICONS[item.key as keyof typeof ICONS] ?? ChartNoAxesCombined;
          return (
            <Link key={item.key} href={item.href} prefetch={false} role="menuitem" onClick={onSelect} className={clsx("flex gap-3 border-l-2 px-3 py-2 transition-colors", isActive(item.href) ? "border-(--color-accent) bg-(--color-brand-light)" : "border-transparent hover:border-(--color-accent) hover:bg-(--color-bg-alt)")}>
              <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.8} aria-hidden />
              <span><strong className="block text-[13px] font-semibold text-(--color-foreground)">{item.label}</strong><span className="mt-0.5 block text-[11px] leading-[17px] text-(--color-muted)">{item.description}</span></span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
